/**
 * „Skala 1–10", „Nagranie · Przysiad ze sztangą" — the mark and the word a question wears.
 *
 * One component for the editor's question rows, the „Nowe pytanie" card's radiogroup and the
 * catalog's rows, because the three draw the same eleven answer types and a second drawing of one
 * is how two surfaces come to disagree about what „Skala" looks like.
 *
 * The WORDS are mobile's verbatim `intakeSurvey.answerType.*`, for the reason `AGENTS.md` gives: the
 * phone names the same eleven types, and a second spelling of one is a divergence waiting to happen.
 * What is web copy is the SUFFIX — the option count and the named exercise — because only this
 * screen says them.
 */

import type { ReactNode } from 'react';

import type { TranslateKey } from '../model/translateKey';
import {
  ClipIcon,
  LongTextIcon,
  MultiChoiceIcon,
  NumberIcon,
  PhotoIcon,
  ScaleIcon,
  ShortTextIcon,
  SingleChoiceIcon,
  TargetIcon,
} from '~/shared/ui/ReportIcons';
import { CalendarIcon } from '~/shared/ui/AuthIcons';
import styles from './EditorForm.module.css';

/** The mark for one `AnswerType`. An unknown type gets the plainest one rather than nothing. */
export function answerTypeIcon(answerType: string | null, className?: string): ReactNode {
  switch (answerType) {
    case 'LongText':
      return <LongTextIcon className={className} />;
    case 'SingleChoice':
      return <SingleChoiceIcon className={className} />;
    case 'MultiChoice':
      return <MultiChoiceIcon className={className} />;
    case 'Number':
      return <NumberIcon className={className} />;
    case 'Scale':
      return <ScaleIcon className={className} />;
    case 'Date':
      return <CalendarIcon className={className} />;
    case 'ExerciseClip':
      return <ClipIcon className={className} />;
    case 'Photo':
      return <PhotoIcon className={className} />;
    case 'GoalBuilder':
      return <TargetIcon className={className} />;
    default:
      return <ShortTextIcon className={className} />;
  }
}

/**
 * The word, plus what only this screen can add to it.
 *
 * A choice question says how many options it offers, and a recording says which movement it asks
 * about — both are facts the trainer needs to tell two otherwise identical rows apart. The exercise
 * NAME arrives resolved: this component does not read, and a component that fetched a name to put
 * in a pill would fetch it once per row.
 */
export function answerTypeWords(
  input: {
    readonly answerType: string | null;
    readonly optionCount?: number;
    readonly exerciseName?: string | null;
  },
  t: TranslateKey,
): string {
  const base = t(`intakeSurvey.answerType.${input.answerType ?? 'ShortText'}`);
  if (input.answerType === 'ExerciseClip') {
    return input.exerciseName == null || input.exerciseName === ''
      ? base
      : t('web:reports.editor.type.clip', { base, exercise: input.exerciseName });
  }
  if (
    (input.answerType === 'SingleChoice' || input.answerType === 'MultiChoice') &&
    (input.optionCount ?? 0) > 0
  ) {
    return t('web:reports.editor.type.options', { base, count: input.optionCount ?? 0 });
  }
  return base;
}

export interface AnswerTypePillProps {
  readonly answerType: string | null;
  readonly optionCount?: number;
  readonly exerciseName?: string | null;
  readonly t: TranslateKey;
}

/** The pill itself: the mark, the word, and the suffix only this screen can say. */
export function AnswerTypePill({ answerType, optionCount, exerciseName, t }: AnswerTypePillProps) {
  return (
    <span className={styles.typePill}>
      {answerTypeIcon(answerType, styles.typePillIcon)}
      {answerTypeWords({ answerType, optionCount, exerciseName }, t)}
    </span>
  );
}
